import React, { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../states/containers/store'
import { ResponseMonthlyTeamReport, ResponseMonthlyMemberReport } from '../../../../../states/types/report'
import Loading from '../../../common/Loading'
import Deviation from '../Deviation'
import DeviationHeader from '../DeviationHeader'
import MonthlyBarGraph from './MonthlyBarGraph'

interface Heatmap {
  type: string
  range: string
  target: string
  id: number
  title: string
  legend: string
  startColor: string
  endColor: string
  minimum: number
  displaySearchLink: boolean
  name: string
}

type ScoreColumnName = 'process_score' | 'resource_score' | 'improvement_score' | 'legacy_score' | 'stress_score'

interface Scores {
  label: ScoreColumnName
  current: number
  past: number | null
  company: number
  qasee?: number
  team?: number
  occupation?: number
  distributions: {
    from: number
    to: number
    percent: number
  }[]
}

interface Props {
  target: string
  heatmapModalOpenCallback: (heatmap: Heatmap) => void,
}

const MonthlyResource: FC<Props> = (props) => {
  const {
    target,
    heatmapModalOpenCallback,
  } = props

  const {
    report,
  } = useSelector((state: RootState) => (state))

  const { t } = useTranslation()

  const subContentsRef = useRef<HTMLDivElement>(null)
  const subContentsLastRef = useRef<HTMLDivElement>(null)

  const [ruledLineHeight, setRuledLineHeight] = useState<number>(0)

  const [data, setData] = useState<ResponseMonthlyTeamReport | ResponseMonthlyMemberReport>()
  const [scores, setScores] = useState<Scores>()
  const [color, setColor] = useState<string>()
  const [workStyleId, setWorkStyleId] = useState<number>(0)
  const [occupationId, setOccupationId] = useState<number>(0)

  const targetIndicatorValues = (indicatorName: string) => ({
    current: data?.indicators?.find((value) => value.label === indicatorName)?.current ?? null,
    prev: data?.indicators?.find((value) => value.label === indicatorName)?.past ?? null,
    team: data?.indicators?.find((value) => value.label === indicatorName)?.team ?? null,
    company: data?.indicators?.find((value) => value.label === indicatorName)?.company ?? null,
    occupation: data?.indicators?.find((value) => value.label === indicatorName)?.occupation ?? null,
    qasee: data?.indicators?.find((value) => value.label === indicatorName)?.qasee ?? null,
    checked: data?.indicators?.find((value) => value.label === indicatorName)?.checked ?? false,
    appropriate_from: data?.indicators?.find((value) => value.label === indicatorName)?.appropriate_from ?? null,
    appropriate_to: data?.indicators?.find((value) => value.label === indicatorName)?.appropriate_to ?? null,
  })

  useEffect(() => {
    if (!subContentsRef.current?.offsetHeight || !subContentsLastRef.current?.offsetHeight) return
    setRuledLineHeight(subContentsRef.current?.offsetHeight - (subContentsLastRef.current?.offsetHeight / 2) + 40)
  }, [subContentsRef.current?.offsetHeight, subContentsLastRef.current?.offsetHeight])

  useEffect(() => {
    if (!target) return
    if (report.isLoading) return
    if (target === "team") {
      if (!report.monthlyTeamReport) return
      setData(report.monthlyTeamReport)
      setScores(report.monthlyTeamReport.scores?.find((score) => score.label === "resource_score"))
      setColor("")
      setWorkStyleId(0)
      setOccupationId(0)
    } else {
      if (!report.monthlyMemberReport) return
      setData(report.monthlyMemberReport)
      setScores(report.monthlyMemberReport.scores?.find((score) => score.label === "resource_score"))
      setColor(report.monthlyMemberReport.member?.applied_occupation?.icon_color)
      setWorkStyleId(0)
      setOccupationId(0)
    }
  }, [target, report])

  return (
    <>
      {
        report.isLoading ? (
          <>
            <Loading />
          </>
        ) : (
          <>
            <section id="monthly_resource" className="page_contents">
              <div
                className="vertical_ruler"
                style={{
                  height: `${ruledLineHeight}px`,
                }}
              />
              <div id="resource" className="content-block monthly">
                <div className="block-detail score">
                  <div className="header_area">
                    <div className="prefix">
                      パフォーマンス
                      <br />
                      【効率度】
                    </div>
                    <div className="title">
                      リソース指数
                    </div>
                    <div className="point">
                      {scores?.current || 0}
                    </div>
                    <div className="description">
                      業務の負荷や働き方の安定度、業務利用デバイスの状況からリソースに対する評価を行っています。
                    </div>
                  </div>
                  <MonthlyBarGraph
                    target={target}
                    value={{
                      current: scores?.current || 0,
                      past: scores?.past || 0,
                      team: scores?.team,
                      occupation: scores?.occupation,
                      company: scores?.company || 0,
                      qasee: scores?.qasee,
                      distributions: scores?.distributions || [],
                    }}
                  />
                </div>
              </div>
            </section>
            <section ref={subContentsRef}>
              <section className="inner_contents">
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        ワークバランス
                      </div>
                    </div>
                    <div className="block-detail">
                      <DeviationHeader
                        target={target}
                        range="monthly"
                        work_style_id={workStyleId}
                        occupation_id={occupationId}
                      />
                      <Deviation
                        target={target}
                        title="stdev_work_time"
                        display_prefix="勤務時間"
                        display_title="時間のばらつき"
                        type="hour"
                        values={targetIndicatorValues("stdev_work_time")}
                        labels={[0, 1, 2, 3, 4, 5, 6]}
                        upper_limit
                        appropriate={33.3}
                        appropriate_position={-50}
                        button={false}
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        help_text={t(`report.indicator_help.monthly.${target}.resource.stdev_work_time`)}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="stdev_biz_point"
                        display_prefix="操作量"
                        display_title="操作のばらつき"
                        type="biz"
                        values={targetIndicatorValues("stdev_biz_point")}
                        labels={[0, 2000, 4000, 6000, 8000, 10000, 12000, 14000, 16000]}
                        upper_limit
                        appropriate={37.5}
                        appropriate_position={-50}
                        button={false}
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        help_text={t(`report.indicator_help.monthly.${target}.resource.stdev_biz_point`)}
                        color={color}
                      />
                    </div>
                  </div>
                </section>
              </section>
              <section className="inner_contents">
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        マンパワー
                      </div>
                    </div>
                    <div className="block-detail">
                      <DeviationHeader
                        target={target}
                        range="monthly"
                        work_style_id={workStyleId}
                        occupation_id={occupationId}
                      />
                      <Deviation
                        target={target}
                        title="overtime_work_time"
                        display_title="残業時間"
                        type="hour"
                        values={targetIndicatorValues("overtime_work_time")}
                        labels={[0, 20, 40, 60, 80]}
                        half
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        help_text={t(`report.indicator_help.monthly.${target}.resource.overtime_work_time`)}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="overtime_work_days"
                        display_title="残業回数"
                        type="count"
                        values={targetIndicatorValues("overtime_work_days")}
                        labels={[0, 5, 10, 15, 20]}
                        half
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        help_text={t(`report.indicator_help.monthly.${target}.resource.overtime_work_days`)}
                        color={color}
                      />
                    </div>
                  </div>
                </section>
              </section>
              <section
                className="inner_contents"
                ref={subContentsLastRef}
              >
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        マシンパワー
                      </div>
                    </div>
                    <div className="block-detail">
                      <DeviationHeader
                        target={target}
                        range="monthly"
                        work_style_id={workStyleId}
                        occupation_id={occupationId}
                      />
                      <Deviation
                        target={target}
                        title="average_cpu_use_rate"
                        display_title="平均CPU使用率"
                        type="percent"
                        values={targetIndicatorValues("average_cpu_use_rate")}
                        labels={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                        appropriate={30}
                        appropriate_position={-50}
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="average_ram_use_rate"
                        display_title="平均メモリ使用率"
                        type="percent"
                        values={targetIndicatorValues("average_ram_use_rate")}
                        labels={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                        appropriate={40}
                        appropriate_position={-10}
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        color={color}
                      />
                    </div>
                  </div>
                </section>
              </section>
            </section>
          </>
        )
      }
    </>
  )
}

export default MonthlyResource
